import { createServerFn } from "@tanstack/react-start";
import { createAuthenticatedApiConfig, collectErrorMessages } from "@/lib/utils.server";
import {
  createBusiness,
  createCategory,
  createType,
  deleteCategory,
  deleteType,
  deleteBusiness,
  getBusiness,
  getBusinesses,
  getCategories,
  getCategory,
  getType,
  getTypes,
  updateBusiness,
  updateCategory,
  updateType,
  getRegions,
  getLanguages,
} from "@/lib/mma";
import {
  BusinessListViewReadable,
  BusinessReadable,
  TypeReadable,
  CategoryReadable,
  BusinessWritable,
  TypeWritable,
  CategoryWritable,
  GetRegionsResponse,
  GetLanguagesResponse,
} from "@/lib/mma/types.gen";

// BUSINESSES

export const getBusinessesFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<BusinessListViewReadable[]> => {
    const config = await createAuthenticatedApiConfig();

    const response = await getBusinesses(config);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get businesses");
    }
    return response.data?.data || [];
  }
);

export const businessesQueryOptions = {
  queryKey: ["businesses"],
  queryFn: getBusinessesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
};

export const getBusinessFn = createServerFn({ method: "GET" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<BusinessReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getBusiness({ ...config, path: { id: data.id } });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get business");
    }
    return response.data?.data || {};
  });

export const businessQueryOptions = (id: number) => ({
  queryKey: ["business", id],
  queryFn: () => getBusinessFn({ data: { id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  enabled: id > 0,
});

export const createBusinessFn = createServerFn({ method: "POST" })
  .inputValidator((data: { payload: BusinessWritable }) => data)
  .handler(async ({ data }): Promise<BusinessReadable> => {
    const config = await createAuthenticatedApiConfig();
    console.log("createBusinessFn data", data);
    const response = await createBusiness({ ...config, body: data.payload });
    console.log("createBusinessFn response", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to create business");
    }
    return response.data.data || {};
  });

export const updateBusinessFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; payload: BusinessWritable }) => data)
  .handler(async ({ data }): Promise<BusinessReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateBusiness({ ...config, path: { id: data.id }, body: data.payload });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update business");
    }
    return response.data.data || {};
  });

export const deleteBusinessFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteBusiness({ ...config, path: { id: data.id } });
    if ((response as any).response.status !== 204) {
      throw new Error(collectErrorMessages((response as any).error) || "Failed to delete business");
    }
    return { success: true };
  });

// BUSINESS TYPES

export const getBusinessTypesFn = createServerFn({ method: "GET" }).handler(async (): Promise<TypeReadable[]> => {
  const config = await createAuthenticatedApiConfig();

  const response = await getTypes(config);
  if (!response.data) {
    throw new Error(collectErrorMessages(response.error) || "Failed to get types");
  }
  return response.data?.data || [];
});

export const businessTypesQueryOptions = {
  queryKey: ["businessTypes"],
  queryFn: getBusinessTypesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
};

export const getBusinessTypeFn = createServerFn({ method: "GET" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<TypeReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await getType({ ...config, path: { id: data.id } });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get type");
    }
    return response.data?.data || {};
  });

export const businessTypeQueryOptions = (id: number) => ({
  queryKey: ["businessType", id],
  queryFn: () => getBusinessTypeFn({ data: { id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
});

export const createBusinessTypeFn = createServerFn({ method: "POST" })
  .inputValidator((data: { payload: TypeWritable }) => data)
  .handler(async ({ data }): Promise<TypeReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createType({ ...config, body: data.payload });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to create business type");
    }
    return response.data.data || {};
  });

export const updateBusinessTypeFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; payload: TypeWritable }) => data)
  .handler(async ({ data }): Promise<TypeReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateType({ ...config, path: { id: data.id }, body: data.payload });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update business type");
    }
    return response.data.data || {};
  });

export const deleteBusinessTypeFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteType({ ...config, path: { id: data.id } });
    if ((response as any).response.status !== 204) {
      throw new Error(collectErrorMessages((response as any).error) || "Failed to delete business type");
    }
    return { success: true };
  });

// BUSINESS CATEGORIES

export const getBusinessCategoriesFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<CategoryReadable[]> => {
    const config = await createAuthenticatedApiConfig();

    const response = await getCategories(config);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get categories");
    }
    return response.data?.data || [];
  }
);

export const businessCategoriesQueryOptions = {
  queryKey: ["businessCategories"],
  queryFn: getBusinessCategoriesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
};

export const getBusinessCategoryFn = createServerFn({ method: "GET" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<CategoryReadable> => {
    const config = await createAuthenticatedApiConfig();

    const response = await getCategory({ ...config, path: { id: data.id } });
    console.log("getBusinessCategoryFn response", response);
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to get category");
    }
    return response.data?.data || {};
  });

export const businessCategoryQueryOptions = (id: number) => ({
  queryKey: ["businessCategory", id],
  queryFn: () => getBusinessCategoryFn({ data: { id } }),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  enabled: id > 0,
});

export const createBusinessCategoryFn = createServerFn({ method: "POST" })
  .inputValidator((data: { payload: CategoryWritable }) => data)
  .handler(async ({ data }): Promise<CategoryReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await createCategory({ ...config, body: data.payload });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to create business category");
    }
    return response.data.data || {};
  });

export const updateBusinessCategoryFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number; payload: CategoryWritable }) => data)
  .handler(async ({ data }): Promise<CategoryReadable> => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateCategory({ ...config, path: { id: data.id }, body: data.payload });
    if (!response.data) {
      throw new Error(collectErrorMessages(response.error) || "Failed to update business category");
    }
    return response.data.data || {};
  });

export const deleteBusinessCategoryFn = createServerFn({ method: "POST" })
  .inputValidator((data: { id: number }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    const response = await deleteCategory({ ...config, path: { id: data.id } });
    console.log("deleteBusinessCategoryFn response", response);
    if ((response as any).response.status !== 204) {
      throw new Error(collectErrorMessages((response as any).error) || "Failed to delete business category");
    }
    return { success: true };
  });

export const getRegionsFn = createServerFn({ method: "GET" }).handler(async (): Promise<string[]> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getRegions(config);
  console.log("getRegionsFn response", response);
  if (!response.data) {
    throw new Error(collectErrorMessages(response.error) || "Failed to get regions");
  }
  return response.data.data || [];
});

export const regionsQueryOptions = {
  queryKey: ["regions"],
  queryFn: getRegionsFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
};

export const getLanguagesFn = createServerFn({ method: "GET" }).handler(async (): Promise<string[]> => {
  const config = await createAuthenticatedApiConfig();
  const response = await getLanguages(config);
  if (!response.data) {
    throw new Error(collectErrorMessages(response.error) || "Failed to get languages");
  }
  return response.data?.data || [];
});

export const languagesQueryOptions = {
  queryKey: ["languages"],
  queryFn: getLanguagesFn,
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: "always" as const,
  refetchOnReconnect: false,
};
